<script setup lang="ts">
import { watch, computed } from 'vue';

import type { FerryProductCardData, FerryUpsellType } from '../../../models';
import { useUpsellStore } from '../../../stores';
import DateCardTag from '../DateCardTag.vue';

import FerryCardHeader from './FerryCardHeader.vue';
import FerryCardRouteDetails from './FerryCardRouteDetails.vue';
import FerryCardTags from './FerryCardTags.vue';
import FerryCardFerryDetails from './FerryCardFerryDetails.vue';
import FerryCardUpsellLoader from './FerryCardUpsellLoader.vue';
import FerryCardUpsell from './FerryCardUpsell.vue';

interface FerryProductCardContentProps {
  productCardContent: FerryProductCardData;
  printMode: boolean;
}

const props = defineProps<FerryProductCardContentProps>();

interface FerryProductCardContentEmits {
  (event: 'change-upsell', upsellType: FerryUpsellType): void;
  (event: 'show-details'): void;
}

const emit = defineEmits<FerryProductCardContentEmits>();

const upsellStore = useUpsellStore();

function onContentClick(): void {
  emit('show-details');
}

const isLoading = computed<boolean>(() => {
  if (props.productCardContent.upsell.ingredientId) {
    return upsellStore.isLoadingDictionary.has(
      props.productCardContent.upsell.ingredientId
    );
  }

  return false;
});

const allTickets = computed(() =>
  upsellStore.getTicketCategories(props.productCardContent.upsell.token)
);

const baseTicketPrice = computed<number>(() => {
  // FYI: For initial display of ticket category prices we need to use base price as seleted price is 0.
  // In new flow, we are no longer comparing to base price as old flow was showing relative price only for flexi tickets.
  // New flow needs to show both relative prices
  if (
    props.productCardContent.upsell.ticket.price !== undefined &&
    props.productCardContent.upsell.ticket.basePrice !== undefined
  ) {
    return props.productCardContent.upsell.ticket.price === 0
      ? props.productCardContent.upsell.ticket.basePrice
      : props.productCardContent.upsell.ticket.price;
  }
  return 0;
});

watch(
  props,
  () => {
    upsellStore.fetchTicketCategories(
      props.productCardContent.upsell.token,
      props.productCardContent.upsell.ingredientId
    );
  },
  { immediate: true }
);

function selectTicketCategory(category: string): void {
  upsellStore.currentFerryTicketCategoryEditionKey =
    props.productCardContent.upsell.token;
  upsellStore.currentFerryTicketEditionIngredientId =
    props.productCardContent.upsell.ingredientId;
  upsellStore.setUserSelectionFromModal(category);
}
</script>

<template>
  <div class="ferry-product-card-content">
    <div class="ferry-content">
      <FerryCardHeader
        :logo-urls="props.productCardContent.details.logo.urls"
        :from-port-label="props.productCardContent.fromPort.country"
        :to-port-label="props.productCardContent.toPort.country"
        @show-details="onContentClick"
      />

      <div class="middle-section">
        <FerryCardRouteDetails :content="props.productCardContent" />

        <FerryCardTags
          :warning-tags="props.productCardContent.details.warningTags"
        />

        <FerryCardFerryDetails
          :vehicles="props.productCardContent.upsell.vehicles"
          :allow-vehicles="props.productCardContent.upsell.allowVehicles"
          :cabin="props.productCardContent.upsell.cabin"
          :print-mode="props.printMode"
          @change-upsell="emit('change-upsell', $event)"
        />
      </div>

      <div class="right-section">
        <FerryCardUpsellLoader v-if="isLoading" />

        <FerryCardUpsell
          v-else
          :base-ticket-price="baseTicketPrice"
          :tickets="allTickets"
          :selected-ticket-type="props.productCardContent.upsell.ticket.type"
          @select-ticket-category="selectTicketCategory"
        />
      </div>
    </div>
    <DateCardTag :date="props.productCardContent.details.date" />
  </div>
</template>

<style scoped lang="scss">
.ferry-product-card-content {
  padding: var(--spacing-12);
  position: relative;

  .ferry-content {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    align-items: flex-start;

    @include for-less-or-equal-than-599-px {
      gap: var(--spacing-16);
    }

    .middle-section {
      flex: 1;
      display: flex;
      flex-direction: column;
      padding-bottom: var(--spacing-12);
      gap: var(--spacing-12);
      width: 100%;
    }

    .right-section {
      border-top: 1px dashed var(--color-overlay);
      padding-top: var(--spacing-8);
      align-self: stretch;
      width: 100%;
      display: flex;
      align-items: center;
    }
  }
}
</style>
